import {
  Menu,
  Bell,
  LogOut,
  ChevronDown,
  ChevronRight,
  User,
  Settings,
  Sparkles,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import useMonarchStore from '@/stores/monarchStore';
import { useChatStore } from '@/stores/chatStore';
import { NAV_ITEMS } from './Navigation';
import DateRangePicker from '../shared/DateRangePicker';
import ThemeToggle from '../shared/ThemeToggle';

/* ============================================================================
   Glue42 Logo — "Glue" in warm white, "42" with brand gradient
   ============================================================================ */

export function Glue42Logo({ className = 'h-7' }: { className?: string }) {
  return (
    <span className={`inline-flex items-baseline select-none ${className}`}>
      <span
        style={{
          fontSize: 'inherit',
          fontWeight: 200,
          letterSpacing: '-0.5px',
          lineHeight: 1,
          color: 'var(--color-text)',
          fontFamily: "'Albert Sans', -apple-system, BlinkMacSystemFont, sans-serif",
        }}
      >
        Glue
      </span>
      <span
        style={{
          fontSize: 'inherit',
          fontWeight: 800,
          letterSpacing: '-0.5px',
          lineHeight: 1,
          background: 'linear-gradient(135deg, var(--color-purple), var(--color-monarch-gold))',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          fontFamily: "'Albert Sans', -apple-system, BlinkMacSystemFont, sans-serif",
        }}
      >
        42
      </span>
    </span>
  );
}

/* ============================================================================
   Header Component
   ============================================================================ */

export default function Header() {
  const auth = useMonarchStore((s) => s.auth);
  const tenant = useMonarchStore((s) => s.tenant);
  const activeSection = useMonarchStore((s) => s.ui.activeSection);
  const mobileMenuOpen = useMonarchStore((s) => s.ui.mobileMenuOpen);
  const setMobileMenuOpen = useMonarchStore((s) => s.setMobileMenuOpen);
  const notificationsOpen = useMonarchStore((s) => s.ui.notificationsOpen);
  const setNotificationsOpen = useMonarchStore((s) => s.setNotificationsOpen);
  const logout = useMonarchStore((s) => s.logout);
  const [headerVisible, setHeaderVisible] = useState(true);

  useEffect(() => {
    let lastScrollY = window.scrollY;
    const handleScroll = () => {
      const y = window.scrollY;
      if (y < 50) setHeaderVisible(true);
      else if (y > lastScrollY && y > 100) setHeaderVisible(false);
      else if (y < lastScrollY) setHeaderVisible(true);
      lastScrollY = y;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const initials = auth.displayName
    ? auth.displayName.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : 'U';

  const currentSection = NAV_ITEMS.find((item) => item.id === activeSection);

  return (
    <header
      className={`fixed top-0 left-0 right-0 h-16 z-40 bg-[var(--color-header-bg)] border-b border-[var(--color-header-border)] transition-transform duration-300 ${
        headerVisible ? 'translate-y-0' : '-translate-y-full'
      }`}
    >
      <div className="h-full px-6 flex items-center justify-between">
        {/* ---- Left ---- */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg text-text-3 hover:bg-bg-2 hover:text-text transition-colors"
            aria-label="Toggle sidebar"
          >
            <Menu className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-3">
            <Glue42Logo className="text-[22px]" />
            <div className="w-px h-5 bg-border" />
            <span className="text-xs font-light text-text-3 tracking-wide hidden sm:inline">
              Hospitality Intelligence Platform
            </span>
          </div>

          {currentSection && (
            <div className="hidden xl:flex items-center gap-2 text-text-3">
              <ChevronRight className="w-3.5 h-3.5" />
              <currentSection.icon className="w-3.5 h-3.5" />
              <span className="text-xs font-medium text-text-3">{currentSection.label}</span>
            </div>
          )}
        </div>

        {/* ---- Right ---- */}
        <div className="flex items-center gap-2.5">
          <div className="hidden sm:block">
            <DateRangePicker />
          </div>

          <ThemeToggle />
          <AIToggleButton />

          <button
            onClick={() => setNotificationsOpen(!notificationsOpen)}
            className="relative p-2 rounded-lg text-text-3 hover:bg-bg-2 hover:text-text transition-colors"
            aria-label="Notifications"
          >
            <Bell className="w-5 h-5" />
            <div className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-monarch-gold" />
          </button>

          {/* Monarch tenant badge */}
          {tenant && (
            <div className="hidden lg:flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-bg-1 border border-[var(--color-header-border)]">
              <img
                src="/logos/monarch-logo.avif"
                alt="The Monarch"
                className="h-5 w-auto logo-theme-aware"
              />
              <span className="text-[10px] font-medium text-text-3 uppercase tracking-wider">
                {tenant.tenantName}
              </span>
            </div>
          )}

          <UserMenu
            displayName={auth.displayName ?? 'User'}
            email={auth.email ?? ''}
            initials={initials}
            onLogout={logout}
          />
        </div>
      </div>
    </header>
  );
}

/* ============================================================================
   AI Toggle Button
   ============================================================================ */

function AIToggleButton() {
  const toggleChat = useChatStore((s) => s.toggleChat);
  const isOpen = useChatStore((s) => s.isOpen);
  const isThinking = useChatStore((s) => s.isThinking);

  return (
    <button
      onClick={() => toggleChat()}
      className={`
        relative flex items-center gap-2 px-3 py-1.5 rounded-lg
        border transition-colors
        ${isOpen
          ? 'bg-primary/10 border-primary/30 text-primary'
          : 'bg-bg-1 border-[var(--color-header-border)] text-text-3 hover:border-border hover:text-text'
        }
      `}
      aria-label="Toggle AI assistant"
    >
      <Sparkles className="w-4 h-4" />
      <span className="text-xs font-medium hidden sm:inline">AI</span>
      {isThinking && (
        <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-monarch-gold animate-pulse" />
      )}
    </button>
  );
}

/* ============================================================================
   User Menu
   ============================================================================ */

interface UserMenuProps {
  displayName: string;
  email: string;
  initials: string;
  onLogout: () => void;
}

function UserMenu({ displayName, email, initials, onLogout }: UserMenuProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-bg-1 border border-[var(--color-header-border)] hover:border-border transition-colors"
      >
        <div className="w-7 h-7 rounded-full bg-monarch-gold text-white flex items-center justify-center text-[10px] font-bold">
          {initials}
        </div>
        <div className="hidden md:flex flex-col items-start">
          <span className="text-xs font-medium text-text-2 leading-tight">{displayName}</span>
          <span className="text-[10px] text-text-3 leading-tight">{email}</span>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-text-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-60 z-40 rounded-lg border border-[var(--color-header-border)] bg-[var(--color-header-bg)] overflow-hidden shadow-xl">
            <div className="p-4 border-b border-[var(--color-header-border)]">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-monarch-gold text-white flex items-center justify-center text-xs font-bold">
                  {initials}
                </div>
                <div>
                  <p className="text-sm font-medium text-text-2">{displayName}</p>
                  <p className="text-xs text-text-3">{email}</p>
                </div>
              </div>
            </div>
            <div className="p-1.5">
              <button className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-text-3 hover:bg-bg-2 hover:text-text transition-colors">
                <User className="w-4 h-4" />
                <span className="text-sm">Profile</span>
              </button>
              <button className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-text-3 hover:bg-bg-2 hover:text-text transition-colors">
                <Settings className="w-4 h-4" />
                <span className="text-sm">Settings</span>
              </button>
            </div>
            <div className="p-1.5 border-t border-[var(--color-header-border)]">
              <button
                onClick={onLogout}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-danger hover:bg-danger/10 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm">Sign out</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
